/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { PrimaryButton, SearchBox, Stack } from "@fluentui/react";

import * as AwardPeriodActions from "Dataflows/AwardPeriod/AwardPeriodAction";
import * as VisualStudioBenefitActions from "Dataflows/VisualStudioBenefit/VisualStudioBenefitAction";

import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import VisualStudioBenefitsList from "Components/VisualStudioBenefitsList/VisualStudioBenefitsList";
import { OperationStatus } from "Constants/OperationStatus";
import { SubSiteUrlRootPath, TenantTypesEnum } from "Constants/TenantTypes";
import {
  defaultListingPageSize,
  defaultVisualStudioBenefitFilterValues,
  visualStudioBenefitFilterFields,
} from "Constants/VisualStudioBenefitConstants";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  FlexRowAlignedCenter,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import {
  getCurrentPage,
  getFileDownloadData,
  getFileDownloadStatus,
  getFilteredCount,
  getLoadVisualStudioBenefitsError,
  getPageSize,
  getSearchText,
  getVisualStudioBenefits,
  getVisualStudioBenefitsFilterValues,
  isLoadingVisualStudioBenefits,
} from "Dataflows/VisualStudioBenefit/VisualStudioBenefitSelector";
import { IFilterValue } from "Interfaces/IFilterValue";
import { constructCsvDownload } from "Utility/DownloadHelper";

import { IVisualStudioBenefitsContainerMatchProps } from "./IVisualStudioBenefitsContainerMatchProps";

const VisualStudioBenefitContainer: React.FunctionComponent = (): ReactElement => {
  const dispatch = useDispatch();

  const benefits = useSelector(getVisualStudioBenefits);
  const currentPage = useSelector(getCurrentPage);
  const filteredCount = useSelector(getFilteredCount);
  const fileDownloadData = useSelector(getFileDownloadData);
  const fileDownloadStatus = useSelector(getFileDownloadStatus);
  const isLoading = useSelector(isLoadingVisualStudioBenefits);
  const loadVisualStudioBenefitsError = useSelector(getLoadVisualStudioBenefitsError);
  const pageSize = useSelector(getPageSize);
  const searchText = useSelector(getSearchText);
  const visualStudioBenefitsFilterValues = useSelector(getVisualStudioBenefitsFilterValues);

  const [searchTextState, setSearchTextState] = useState(searchText);

  const routeParams = useParams<IVisualStudioBenefitsContainerMatchProps>();

  const tenant =
    SubSiteUrlRootPath[routeParams.tenant] === TenantTypesEnum.MSP
      ? TenantTypesEnum.MLSA
      : SubSiteUrlRootPath[routeParams.tenant];

  const handleSearch = (
    searchValue: string,
    selectedvisualStudioBenefitsFilterValues: IFilterValue[],
    page: number,
    pageSizeLocal: number
  ): void => {
    dispatch(
      VisualStudioBenefitActions.searchVisualStudioBenefits(
        tenant,
        searchValue,
        selectedvisualStudioBenefitsFilterValues,
        page,
        pageSizeLocal
      )
    );
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    /**
     * NOTE: The award period endpoints expect "MSP" as the
     * student ambassadors tenant; the visual studio benefits endpoint
     * expects "MLSA" as the student ambassadors tenant. We need both
     * of these in this file but this is the only spot that needs to
     * send "MSP".
     */
    if (tenant === TenantTypesEnum.MLSA) {
      dispatch(AwardPeriodActions.getAwardPeriods(TenantTypesEnum.MSP));
    } else {
      dispatch(AwardPeriodActions.getAwardPeriods(tenant));
    }

    /**
     * Set currentPage and pageSize back to default
     * values when switching between tenants.
     */
    dispatch(VisualStudioBenefitActions.updateCurrentPage(1));
    dispatch(VisualStudioBenefitActions.updatePageSize(16));

    /**
     * Clear filter values when we switch between tenants.
     */
    dispatch(VisualStudioBenefitActions.clearVisualStudioBenefitsFilterValue());

    /**
     * Reset search text when we switch between tenants
     */
    setSearchTextState("");

    /**
     * Trigger search with default values
     */
    handleSearch("", defaultVisualStudioBenefitFilterValues, 1, defaultListingPageSize);
  };

  useEffect(() => {
    resetFilterValues();
  }, [tenant]);

  useEffect(() => {
    handleSearch(searchTextState, visualStudioBenefitsFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize]);

  const onClear = (): void => {
    handleSearch("", visualStudioBenefitsFilterValues, 1, pageSize);
  };

  const downloadCSVFile = (file: string): void => {
    constructCsvDownload(
      file,
      `${tenant.toLowerCase()}_visual_studio_benefit_requests_${new Date().toLocaleString()}.csv`
    );
  };

  useEffect(() => {
    if (fileDownloadStatus === OperationStatus.Succeeded && !!fileDownloadData) {
      downloadCSVFile(fileDownloadData);
      dispatch(VisualStudioBenefitActions.downloadRequestsToCSVClean());
    }
  }, [fileDownloadStatus, fileDownloadData]);

  const handleDownloadRequests = () => {
    dispatch(VisualStudioBenefitActions.downloadRequestsToCSV(tenant));
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={(filterValue) =>
            dispatch(VisualStudioBenefitActions.addVisualStudioBenefitsFilterValue(filterValue))
          }
          removeFilterValue={(filterValue) =>
            dispatch(VisualStudioBenefitActions.removeVisualStudioBenefitsFilterValue(filterValue))
          }
          updateFilterValue={(filterValue) =>
            dispatch(VisualStudioBenefitActions.updateVisualStudioBenefitsFilterValue(filterValue))
          }
          selectedFilterValues={visualStudioBenefitsFilterValues}
          filterFields={visualStudioBenefitFilterFields}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void =>
              handleSearch(searchTextState, visualStudioBenefitsFilterValues, 1, pageSize)
            }
          />
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Reset"
            onClick={(): void => resetFilterValues()}
          />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>Benefits - Visual Studio</Title>
            <FlexRowAlignedCenter>
              <ListCounts total={filteredCount} loading={isLoading} />
              {tenant !== TenantTypesEnum.MLSA && (
                <PrimaryButton
                  text="Requests"
                  iconProps={{ iconName: "download" }}
                  onClick={handleDownloadRequests}
                />
              )}
            </FlexRowAlignedCenter>
          </PaddedFlexRow>
          <SearchBox
            placeholder="Search benefits"
            value={searchTextState}
            onSearch={(searchValue: string): void =>
              handleSearch(searchValue, visualStudioBenefitsFilterValues, 1, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchTextState(newValue || "")
            }
            onClear={onClear}
            styles={searchBoxStyles}
          />
          <VisualStudioBenefitsList
            data={benefits}
            filteredCount={filteredCount}
            pageSize={pageSize}
            isLoadingBenefits={isLoading}
            currentPage={currentPage}
            loadBenefitsError={loadVisualStudioBenefitsError}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

export default VisualStudioBenefitContainer;
